import { makeVar } from "@apollo/client";
import { useEffect } from "react";
import { useRouter } from "next/router";
import { DETAIL_HEADER_ID } from "~/components/blocks/product/detail/header";
import { resetAllProductState } from "~/components/stores/detail-product/store";
import { PATHS } from "~/const/paths";
import { useQueryString } from "~/hooks/use-query-string";
import type { G_GetVideoQuery } from "~/lib/graphql";
import { useGetVideoQuery } from "~/lib/graphql";
import { PAGE_TYPES } from "~/lib/tracking/const";
import { sendTrackingPageView } from "~/lib/tracking/events";
import { useAuth } from "~/provider/auth";
import { getDevice, getPlayDevice } from "~/util/get-device";
import { isBot } from "~/util/is-bot";

export const videoVar = makeVar<G_GetVideoQuery | undefined>(undefined);

export const useDetailProduct = () => {
  const { isReady, asPath } = useRouter();
  const { season, content } = useQueryString();
  const { isLoggedIn } = useAuth();
  const { loading: isVideoLoading, data: videoData } = useGetVideoQuery({
    fetchPolicy: "network-only",
    skip: !isReady || season === undefined,
    ssr: isBot(),
    variables: {
      seasonId: season,
      contentId: content ?? "",
      device: getDevice(),
      playDevice: getPlayDevice(),
      isLoggedIn,
      isContentId: content !== undefined,
    },
    onCompleted: (data) => {
      videoVar(data);
    },
  });

  // MEMO : 作品末端を全画面（/vod/detail）で開いた時にスクロール位置がトップにならないため、以下の処理を追加
  const effectScrollTopMove = () => {
    if (isVideoLoading || !(asPath.includes(PATHS.VOD_DETAIL) || asPath.includes(PATHS.VOD_GRAVURE_DETAIL))) return;

    document.documentElement.scrollTo({ top: 0, left: 0 });
  };

  // MEMO : 関連商品を押下時にModal内でトップまでスクロールするように制御を加えている関数
  // FIXME（kimura） : Enterキーボード押下時にModal内のトップにscrollしないので「UI Library」側の改修が必要
  const effectModalScrollTopMove = () => {
    if (isVideoLoading || season === undefined || !isReady) return;

    const detailHeaderDivElement = document.querySelector(`#${DETAIL_HEADER_ID}`);
    if (detailHeaderDivElement === null) return;
    detailHeaderDivElement.scrollIntoView({ block: "start" });
  };

  const effectResetAllProductState = () => {
    return () => {
      resetAllProductState();
    };
  };

  const effectPageType = () => {
    sendTrackingPageView(PAGE_TYPES.DETAIL);
  };

  useEffect(effectScrollTopMove, [isVideoLoading]);
  useEffect(effectModalScrollTopMove, [isReady, season, isVideoLoading]);
  useEffect(effectResetAllProductState, []);
  useEffect(effectPageType, [window.i3, asPath]);

  return {
    isVideoLoading,
    videoData,
  };
};
